import { useState } from 'react'
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { colors, fontWeight, radius } from '../tokens'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Props = {
  qualifiesForFormalProposal: boolean
  submitting: boolean
  errorMessage?: string
  onSubmit: (values: { name: string; email: string; address?: string }) => void
}

// Lightweight precondition form: we need the proposer's name on the email
// to the VOA before we can render the stepper, so this gates the pack
// generation. Once submitted the parent swaps in the full stepper view.
export const ProposerDetailsForm = ({
  qualifiesForFormalProposal,
  submitting,
  errorMessage,
  onSubmit,
}: Props) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [contactAddress, setContactAddress] = useState('')

  const emailValid = EMAIL.test(email.trim())
  const showEmailError = emailTouched && email.trim().length > 0 && !emailValid
  const canSubmit = name.trim().length > 0 && emailValid && !submitting

  return (
    <View style={styles.card}>
      <Text style={styles.title}>One last detail</Text>
      <Text style={styles.subtitle}>
        {qualifiesForFormalProposal
          ? `We'll put your name on the VO 7455 form and the Band Review email so the Valuation Office knows who's sending it.`
          : `We'll put your name on the Band Review email so the Valuation Office knows who's sending it.`}
      </Text>

      <Text style={styles.label}>Your full name</Text>
      <TextInput
        accessibilityLabel="Proposer name"
        autoComplete="name"
        editable={!submitting}
        onChangeText={setName}
        placeholder="e.g. Alex Smith"
        style={styles.input}
        value={name}
      />

      <Text style={styles.label}>Your email</Text>
      <TextInput
        accessibilityLabel="Proposer email"
        autoCapitalize="none"
        autoComplete="email"
        editable={!submitting}
        keyboardType="email-address"
        onBlur={() => setEmailTouched(true)}
        onChangeText={setEmail}
        placeholder="you@example.com"
        style={[styles.input, showEmailError && styles.inputError]}
        value={email}
      />
      {showEmailError ? (
        <Text style={styles.fieldError}>Enter a valid email, e.g. you@example.com.</Text>
      ) : null}

      <Text style={styles.label}>Your contact address (optional)</Text>
      <TextInput
        accessibilityLabel="Proposer contact address"
        autoComplete="postal-address"
        editable={!submitting}
        multiline
        numberOfLines={2}
        onChangeText={setContactAddress}
        placeholder="Leave blank if same as the property above. Landlords / agents — enter your address here."
        style={[styles.input, styles.inputMultiline]}
        value={contactAddress}
      />

      <Pressable
        accessibilityRole="button"
        disabled={!canSubmit}
        onPress={() =>
          onSubmit({
            name: name.trim(),
            email: email.trim(),
            address: contactAddress.trim() || undefined,
          })
        }
        style={[styles.button, !canSubmit && styles.buttonDisabled]}
      >
        <Text style={styles.buttonText}>
          {submitting ? 'Generating your pack…' : 'Generate appeal package'}
        </Text>
      </Pressable>
      {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    marginTop: 20,
    padding: 22,
    borderRadius: radius.lg,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 6,
  },
  title: { fontSize: 19, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.3 },
  subtitle: { fontSize: 14, color: colors.muted, lineHeight: 21, marginBottom: 4 },
  label: {
    fontSize: 13,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
    marginTop: 12,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: colors.surface,
    color: colors.ink,
  },
  inputMultiline: { minHeight: 64, textAlignVertical: 'top' },
  button: {
    marginTop: 16,
    backgroundColor: colors.primary[700],
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonDisabled: { backgroundColor: colors.surface2 },
  buttonText: { color: '#fff', fontSize: 15.5, fontWeight: fontWeight.semibold },
  error: { color: colors.error[700], fontSize: 13, marginTop: 8 },
  inputError: { borderColor: colors.error[700], borderWidth: 2 },
  fieldError: { color: colors.error[700], fontSize: 13, marginTop: 6 },
})
